import behaviourPositiveBehaviourConstants from "@/constants/behaviourPositiveBehaviourConstants.js"

const {
  MAX_NAME_LENGTH,
  MAX_AWARD_DESCRIPTION_LENGTH,
  POINTS_MIN,
  POINTS_MAX
} = behaviourPositiveBehaviourConstants

/**
 * @returns {{
 *   name: string
 *   core_value_id: string | number
 *   award_description: string
 *   points_awarded: string
 *   status: number
 * }}
 */
export function createEmptyPositiveBehaviourForm() {
  return {
    name: "",
    core_value_id: "",
    award_description: "",
    points_awarded: "",
    status: 1
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {ReturnType<typeof createEmptyPositiveBehaviourForm>}
 */
export function positiveBehaviourRowToForm(row) {
  if (!row) return createEmptyPositiveBehaviourForm()
  const isActive =
    row.status === "active" || row.status === 1 || row.status === true
  return {
    name: String(row.name ?? ""),
    core_value_id: row.coreValueId ?? row.core_value_id ?? "",
    award_description: String(
      row.awardDescription ?? row.award_description ?? row.description ?? ""
    ),
    points_awarded: row.points != null ? String(row.points) : "",
    sort_order: row?.sort_order ?? null,
    status: isActive ? 1 : 0
  }
}

/**
 * @param {{ name?: string, core_value_id?: string|number, award_description?: string, points_awarded?: string|number, status?: number|string }} form
 * @param {string[]} [existingNames]
 * @param {{ excludeName?: string }} [options]
 * @returns {Record<string, string>}
 */
export function validatePositiveBehaviourForm(
  form,
  existingNames = [],
  options = {}
) {
  const excludeName = options?.excludeName
  const errors = {}
  const name = String(form?.name ?? "").trim()
  const awardDescription = String(form?.award_description ?? "").trim()
  const coreValueId = form?.core_value_id
  const points = Number(form?.points_awarded)

  if (!name) {
    errors.name = "required"
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.name = "maxLength"
  } else {
    const excludeNormalized = String(excludeName ?? "")
      .trim()
      .toLowerCase()
    const duplicate = (existingNames || []).some((n) => {
      const normalized = String(n || "")
        .trim()
        .toLowerCase()
      if (excludeNormalized && normalized === excludeNormalized) {
        return false
      }
      return normalized === name.toLowerCase()
    })
    if (duplicate) {
      errors.name = "duplicate"
    }
  }

  if (coreValueId == null || coreValueId === "") {
    errors.core_value_id = "required"
  }

  if (!awardDescription) {
    errors.award_description = "required"
  } else if (awardDescription.length > MAX_AWARD_DESCRIPTION_LENGTH) {
    errors.award_description = "maxLength"
  }

  const status = Number(form?.status)
  if (
    form?.status == null ||
    form?.status === "" ||
    (status !== 0 && status !== 1)
  ) {
    errors.status = "required"
  }

  if (
    form?.points_awarded == null ||
    String(form.points_awarded).trim() === ""
  ) {
    errors.points_awarded = "required"
  } else if (
    !Number.isFinite(points) ||
    points < POINTS_MIN ||
    points > POINTS_MAX
  ) {
    errors.points_awarded = "range"
  }

  return errors
}

/**
 * @param {Record<string, unknown>} form
 * @returns {{
 *   name: string
 *   core_value_id: number
 *   points_awarded: number
 *   description: string | null
 *   status: number
 * }}
 */
export function buildPositiveBehaviourCreatePayload(form) {
  const description = String(form?.award_description ?? "").trim()
  return {
    name: String(form?.name ?? "").trim(),
    core_value_id: Number(form?.core_value_id),
    points_awarded: Number(form?.points_awarded),
    description: description || null,
    status: Number(form?.status) === 0 ? 0 : 1
  }
}

/**
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} [existingRow]
 */
export function buildPositiveBehaviourUpdatePayload(form, existingRow = {}) {
  const sortRaw = form?.sort_order ?? existingRow?.sort_order
  const sortOrder =
    sortRaw != null && sortRaw !== "" && Number.isFinite(Number(sortRaw))
      ? Number(sortRaw)
      : null

  return {
    ...buildPositiveBehaviourCreatePayload(form),
    sort_order: sortOrder
  }
}

/**
 * PUT payload for enable/disable: same fields as edit, only `status` changes (0 or 1).
 * @param {Record<string, unknown>} existingRow
 * @param {"active" | "inactive" | 0 | 1 | boolean} nextStatus
 */
export function buildPositiveBehaviourStatusUpdatePayload(
  existingRow,
  nextStatus
) {
  const form = positiveBehaviourRowToForm(existingRow)
  const enabled =
    nextStatus === "active" || nextStatus === 1 || nextStatus === true
  form.status = enabled ? 1 : 0
  return buildPositiveBehaviourUpdatePayload(form, existingRow)
}

/**
 * @param {unknown} err
 * @returns {{ field: string | null, code: string, message: string }}
 */
export function parsePositiveBehaviourApiError(err) {
  const data = err?.response?.data
  const message =
    String(data?.error ?? data?.message ?? err?.message ?? "").trim() || ""
  const normalized = message.toLowerCase()

  if (
    normalized.includes("already exists") ||
    normalized.includes("duplicate")
  ) {
    return { field: "name", code: "duplicate", message }
  }

  return { field: null, code: "server", message }
}

/**
 * @param {Record<string, unknown>} form
 * @param {number} nextId
 * @param {{ id?: number, name?: string } | null} [coreValue]
 * @returns {Record<string, unknown>}
 */
export function buildPositiveBehaviourRowFromForm(form, nextId, coreValue) {
  const isInactive =
    form?.status === 0 || form?.status === false || form?.status === "inactive"
  const pointsValue = Number(form?.points_awarded)
  return {
    id: nextId,
    name: String(form?.name ?? "").trim(),
    awardDescription: String(form?.award_description ?? "").trim(),
    points:
      Number.isFinite(pointsValue) && pointsValue >= POINTS_MIN
        ? pointsValue
        : POINTS_MIN,
    coreValueId: Number(form?.core_value_id) || coreValue?.id,
    coreValueName: coreValue?.name || "",
    status: isInactive ? "inactive" : "active"
  }
}

/**
 * @param {Record<string, unknown>} existingRow
 * @param {Record<string, unknown>} form
 * @param {{ id?: number, name?: string } | null} [coreValue]
 */
export function updatePositiveBehaviourRowFromForm(
  existingRow,
  form,
  coreValue
) {
  if (!existingRow?.id) {
    return buildPositiveBehaviourRowFromForm(
      form,
      getNextPositiveBehaviourId([]),
      coreValue
    )
  }
  return {
    ...existingRow,
    ...buildPositiveBehaviourRowFromForm(
      { ...form, points_awarded: form?.points_awarded ?? existingRow.points },
      existingRow.id,
      coreValue
    )
  }
}

/**
 * @param {Array<{ id?: number }>} rows
 * @returns {number}
 */
export function getNextPositiveBehaviourId(rows) {
  const list = Array.isArray(rows) ? rows : []
  const maxId = list.reduce(
    (max, row) => Math.max(max, Number(row?.id) || 0),
    0
  )
  return maxId + 1
}
